import {Inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {
  LegalEntity,
  LegalEntityCreateRequest,
  LegalEntityCreateResponse,
  LegalEntityDetails,
  LegalEntityUpdateRequest,
  LegalEntityUpdateResponse
} from '@ms-organization-entities';
import {ApiClientConfig} from '../api-client-config';
import {CanCreateResponse, LegalEntityDependency, PriorityPatchRequest} from '@primatoo-ms-organization';
import {DataProtectionGovernanceService} from '../interface/data-protection-governance-service';
import {
  ListLegalEntityForMenuResponse
} from "it/primatoo/openapi/api/org/schemas/legalentity/ListLegalEntityForMenuResponse";
import {ApiClientUtils} from "../../api-client.utils";

@Injectable({
  providedIn: 'root'
})
export class LegalEntityService extends DataProtectionGovernanceService {
  private readonly endpoint: string;
  private readonly endpointV2: string;

  constructor(
    @Inject(ApiClientConfig) private config,
    public httpClient: HttpClient
  ) {
    super(httpClient);
    this.endpoint = this.config.endPoint + '/api/org/legal-entities/';
    this.endpointV2 = this.config.endPoint + '/api/org/v2/legal-entities/';

  }

  // API V2
  public getMenu(onlyInternal?: boolean): Observable<ListLegalEntityForMenuResponse> {
    const url = `${this.endpointV2}menu/`;
    const params = ApiClientUtils.buildAndAppendHttpParams({onlyInternal: onlyInternal});
    return this.httpClient.get<ListLegalEntityForMenuResponse>(url, {params});
  }

  // API V1
  public setPriority(id: number, request: PriorityPatchRequest): Observable<LegalEntity> {
    const url = `${this.endpoint}${id}/priority`;
    return this.httpClient.patch<LegalEntity>(url, request);
  }

  // public updateUserColumns(columnsConfigurationUpsertRequest: ColumnsConfigurationUpsertRequest): Observable<void> {
  //   const url = this.endpoint + 'user-columns';
  //   return this.httpClient.post<void>(url, columnsConfigurationUpsertRequest);
  // }

  public getLegalEntities(): Observable<LegalEntity[]> {
    const url = this.endpoint;
    return this.httpClient.get<LegalEntity[]>(url);
  }

  public getInternalLegalEntities(): Observable<LegalEntity[]> {
    const url = this.endpoint + 'internal';
    return this.httpClient.get<LegalEntity[]>(url);
  }

  public getLegalEntitiesById(id: number): Observable<LegalEntity> {
    const url = this.endpoint + id + '/';
    return this.httpClient.get<LegalEntity>(url);
  }

  public deleteLegalEntity(id: number): Observable<void> {
    const url = this.endpoint + id + '/';
    return this.httpClient.delete<void>(url);
  }

  public createLegalEntity(requestBody: LegalEntityCreateRequest): Observable<LegalEntityCreateResponse> {
    const url = this.endpoint;
    return this.httpClient.post<LegalEntityCreateResponse>(url, requestBody);
  }

  public updateLegalEntity(id: number, requestBody: LegalEntityUpdateRequest): Observable<LegalEntityUpdateResponse> {
    const url = this.endpoint + id + '/';
    return this.httpClient.put<LegalEntityUpdateResponse>(url, requestBody);
  }

  public getLegalEntityDetail(id: number): Observable<LegalEntityDetails> {
    const url = this.endpoint + id + '/details';
    return this.httpClient.get<LegalEntityDetails>(url, {});
  }

  public getLegalEntityDependencies(id: number): Observable<LegalEntityDependency> {
    const url = this.endpoint + id + '/dependency';
    return this.httpClient.get<LegalEntityDependency>(url, {});
  }

  public canCreateLegalEntity(): Observable<CanCreateResponse> {
    const url = this.endpoint + `internal/can-create`;
    return this.httpClient.get<CanCreateResponse>(url);
  }

  exportRaw() {
    const url = this.endpoint + `download`;
    return this.httpClient.get(url, {
      responseType: 'blob',
      observe: 'response'
    });
  }

  getEndPointV1(): string {
    return this.endpoint;
  }
}
